import { FormLabel, IconButton, SxProps } from "@mui/material";
import {
  ReactNode,
  TextareaHTMLAttributes,
  useContext,
  ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { FormContext } from "../../../context";
import { getErrorMessage } from "../../../utils/FormValidationsUtils";
import { Box } from "../Box";
import { Label } from "../Typography";
import styles from "./TextArea.module.scss";
import { Icon } from "../Icon";
import { palette } from "../../../styles/theme";

interface Props
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  label?: string;
  link?: ReactNode;
  error?: string;
  name?: string;
  containerSx?: SxProps;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>, value?: string) => void;
  labelSx?: SxProps;
  noMaxWidth?: boolean;
  noDefaultValue?: boolean;
  clearable?: boolean;
  "data-test"?: string;
  minRows?: number;
  fullWidth?: boolean;
}

export const TextArea = ({
  label,
  link,
  name = "",
  labelSx,
  containerSx,
  noMaxWidth,
  noDefaultValue,
  clearable,
  minRows,
  fullWidth,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { formik } = useContext(FormContext);
  const hasError = !!props.error || !!formik?.errors[name];
  const isTouched = formik?.touched[name];

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }
    formik
      ? formik?.setFieldValue(name, event.target.value)
      : props.onChange?.(event);
  };

  return (
    <Box
      width="100%"
      maxWidth={noMaxWidth ? undefined : "400px"}
      display="flex"
      justifyItems="center"
      flexDirection="column"
      minWidth={props.style?.width === "100%" ? "100%" : undefined}
      sx={containerSx}
      position="relative"
      fullWidth={fullWidth}
    >
      {link ? (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          {!!label && <Label htmlFor={props.id}>{label}</Label>}
          {link}
        </Box>
      ) : (
        !!label && (
          <Label
            htmlFor={props.id}
            sx={{ mb: 0.5, textAlign: "start", ...labelSx }}
          >
            {label}
          </Label>
        )
      )}

      <textarea
        onChange={handleChange}
        defaultValue={
          !props.value && !noDefaultValue
            ? formik?.initialValues[name]
            : undefined
        }
        {...props}
        name={name}
        className={`${styles.textArea} ${!!hasError && !!isTouched ? styles.error : ""} ${fullWidth ? "fullWidth" : ""}`}
        data-test={props["data-test"]}
        style={{
          ...props.style,
          resize: "vertical",
          minHeight: `${minRows ? minRows * 2 : 0}em`, // Approximate line height
        }}
      />

      {clearable && (
        <IconButton
          sx={{
            position: "absolute",
            right: "8px",
            top: label ? "40px" : "8px",
          }}
          onClick={() =>
            handleChange({
              target: { value: "" },
            } as ChangeEvent<HTMLTextAreaElement>)
          }
        >
          <Icon
            icon="clear"
            size={16}
            weight={500}
            color={palette.grey.primary}
          />
        </IconButton>
      )}

      {!!formik?.errors[name] && !!isTouched && (
        <FormLabel sx={{ textAlign: "start" }} color="error" error>
          {getErrorMessage(t, formik?.errors[name])}
        </FormLabel>
      )}
      {!!props.error && (
        <FormLabel sx={{ textAlign: "start" }} color="error" error>
          {getErrorMessage(t, props.error)}
        </FormLabel>
      )}
    </Box>
  );
};
